import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { servicesAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ServiceCard from "@/components/ServiceCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Store, Star, Pencil, Wrench, Search } from "lucide-react";
import ReviewsSection from "@/components/ReviewsSection";

export default function Storefront() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    setLoading(true);
    servicesAPI.getStorefront(userId).then((r) => setStore(r.data)).catch(() => setStore(null)).finally(() => setLoading(false));
    servicesAPI.getCategories().then((r) => setCats(r.data?.categories || [])).catch(() => {});
  }, [userId]);

  const catLabel = (slug) => cats.find((c) => c.slug === slug)?.label || slug;

  if (loading) return <div className="mx-auto max-w-5xl space-y-4"><Skeleton className="h-40 w-full rounded-xl" /><Skeleton className="h-6 w-48" /></div>;
  if (!store) return <div className="mx-auto max-w-5xl py-20 text-center"><p className="text-lg font-medium">Storefront not found</p></div>;

  const name = store.brand_name || store.username;
  const isOwner = user?.id === store.user_id;
  const Section = ({ title, Icon, items }) => (
    items?.length ? (
      <div className="space-y-3">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Icon className="h-4 w-4 text-primary" /> {title}</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((l) => <ServiceCard key={l.id} listing={l} categoryLabel={catLabel} />)}
        </div>
      </div>
    ) : null
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <button onClick={() => navigate("/marketplace?tab=services")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to services
      </button>

      <Card className="overflow-hidden border-border bg-card">
        <div className="relative h-40 bg-secondary sm:h-52">
          {store.cover_image ? (
            <img src={store.cover_image} alt="cover" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/20 to-transparent text-muted-foreground"><Store className="h-10 w-10" /></div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 p-5">
          <Avatar className="h-16 w-16 border-2 border-background -mt-12 bg-card">
            <AvatarImage src={store.profile_image || store.avatar} />
            <AvatarFallback>{name?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold" data-testid="storefront-name">{name}</h1>
            {Number(store.seller_rating) > 0 && (
              <p className="flex items-center gap-1 text-sm text-muted-foreground">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {store.seller_rating} ({store.total_reviews} reviews)
              </p>
            )}
          </div>
          {isOwner && (
            <Button variant="outline" onClick={() => navigate("/storefront/edit")} data-testid="storefront-edit-button">
              <Pencil className="mr-2 h-4 w-4" /> Edit storefront
            </Button>
          )}
        </div>
        {(store.bio || store.services_offered?.length > 0) && (
          <div className="space-y-3 border-t border-border px-5 py-4">
            {store.bio && <p className="text-sm leading-relaxed text-foreground/80">{store.bio}</p>}
            {store.services_offered?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {store.services_offered.map((s, i) => (
                  <span key={i} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium">{s}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {store.gallery?.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-display text-lg font-bold">Previous work</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {store.gallery.map((g, i) => (
              <div key={i} className="aspect-square overflow-hidden rounded-lg border border-border">
                <img src={g} alt="" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      <Section title="Services offered" Icon={Wrench} items={store.offerings} />
      <Section title="Looking for" Icon={Search} items={store.requests} />

      {!store.offerings?.length && !store.requests?.length && (
        <p className="py-10 text-center text-sm text-muted-foreground">No active service listings yet.</p>
      )}

      {(store.service_reviews > 0 || store.product_reviews > 0) && (
        <div className="grid gap-8 md:grid-cols-2">
          {store.service_reviews > 0 && (
            <div><p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">Service reviews</p><ReviewsSection sellerId={store.user_id} kind="service" /></div>
          )}
          {store.product_reviews > 0 && (
            <div><p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">Product reviews</p><ReviewsSection sellerId={store.user_id} kind="product" /></div>
          )}
        </div>
      )}
    </div>
  );
}
